/**
 * Dialog premium para enviar botão PIX via WhatsApp
 * 
 * Permite ao usuário:
 * - Preencher chave PIX, nome do recebedor e tipo de chave
 * - Adicionar mensagem personalizada opcional
 * - O destinatário recebe botão para copiar a chave PIX
 * 
 * Design moderno com gradiente verde (cor do PIX) e animações suaves
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { QrCode, User, Key, Loader2, Send, CreditCard } from 'lucide-react';

// Tipos de chave PIX disponíveis
type PixKeyType = 'CPF' | 'CNPJ' | 'EMAIL' | 'CELULAR' | 'ALEATORIA';

// Configurações para cada tipo de chave (validação e placeholder)
const pixKeyTypeConfig: Record<PixKeyType, { label: string; placeholder: string; mask?: RegExp }> = {
  CPF: {
    label: 'CPF',
    placeholder: '000.000.000-00',
    mask: /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/,
  },
  CNPJ: {
    label: 'CNPJ',
    placeholder: '00.000.000/0000-00',
    mask: /^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}$/,
  },
  EMAIL: {
    label: 'E-mail',
    placeholder: 'email@exemplo.com',
    mask: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  },
  CELULAR: {
    label: 'Celular',
    placeholder: '+55 11 99999-9999',
    mask: /^\+?\d{10,14}$/,
  },
  ALEATORIA: {
    label: 'Chave Aleatória',
    placeholder: 'Ex: a1b2c3d4-e5f6-7890-abcd-ef1234567890',
  },
};

interface SendPixDialogProps {
  /** Se o dialog está aberto */
  open: boolean;
  /** Callback para fechar o dialog */
  onClose: () => void;
  /** Callback para enviar o PIX */
  onSend: (pix: {
    name: string;
    key: string;
    keyType: PixKeyType;
    message?: string;
  }) => Promise<void>;
}

export function SendPixDialog({
  open,
  onClose,
  onSend,
}: SendPixDialogProps) {
  // Estados do formulário
  const [name, setName] = useState('');
  const [pixKey, setPixKey] = useState('');
  const [keyType, setKeyType] = useState<PixKeyType>('ALEATORIA');
  const [message, setMessage] = useState('');
  
  // Estado de envio
  const [sending, setSending] = useState(false);

  // Validação do formulário
  const isValid = name.trim().length >= 3 && pixKey.trim().length > 0;

  // Limpa estados quando fecha o dialog
  useEffect(() => {
    if (!open) {
      setName('');
      setPixKey('');
      setKeyType('ALEATORIA');
      setMessage('');
    }
  }, [open]);

  // Handler para envio
  const handleSend = async () => {
    if (sending || !isValid) return;

    try {
      setSending(true);
      
      await onSend({
        name: name.trim(),
        key: pixKey.trim(),
        keyType,
        message: message.trim() || undefined,
      });
      
      onClose();
    } catch (error) {
      console.error('[SendPixDialog] Erro ao enviar:', error);
    } finally {
      setSending(false);
    }
  };

  // Handler para tecla Enter
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && isValid) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handler para fechar
  const handleClose = () => {
    if (sending) return;
    onClose();
  };

  // Configuração do tipo de chave selecionado
  const currentConfig = pixKeyTypeConfig[keyType];

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent 
        className="sm:max-w-[480px]"
        onInteractOutside={(e) => sending && e.preventDefault()}
        onPointerDownOutside={(e) => sending && e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
      >
        {/* Header com gradiente verde (cor do PIX) */}
        <DialogHeader className="space-y-2 pb-0">
          <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-green-600 shadow-lg shadow-emerald-500/25">
            <QrCode className="h-6 w-6 text-white" />
          </div>
          <div className="text-center space-y-0.5">
            <DialogTitle className="text-lg font-semibold">
              Enviar PIX
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              O destinatário poderá copiar a chave PIX
            </p>
          </div>
        </DialogHeader>

        {/* Formulário */}
        <div className="space-y-4 py-2">
          {/* Nome do recebedor */}
          <div className="space-y-1.5">
            <Label htmlFor="pix-name" className="text-xs font-medium flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <User className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
              </div>
              Nome do Recebedor
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="pix-name"
              placeholder="Ex: João da Silva"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
              autoFocus
              className="h-9 rounded-lg bg-muted/50 border-0 text-sm focus-visible:ring-2 focus-visible:ring-emerald-500 transition-all"
            />
          </div>

          {/* Tipo de chave PIX */}
          <div className="space-y-1.5">
            <Label htmlFor="pix-key-type" className="text-xs font-medium flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CreditCard className="h-3 w-3 text-green-600 dark:text-green-400" />
              </div>
              Tipo de Chave
              <span className="text-destructive">*</span>
            </Label>
            <Select
              value={keyType}
              onValueChange={(value) => setKeyType(value as PixKeyType)}
              disabled={sending}
            >
              <SelectTrigger className="h-9 rounded-lg bg-muted/50 border-0 text-sm focus-visible:ring-2 focus-visible:ring-emerald-500 transition-all">
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(pixKeyTypeConfig).map(([type, config]) => (
                  <SelectItem key={type} value={type}>
                    {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Chave PIX */}
          <div className="space-y-1.5">
            <Label htmlFor="pix-key" className="text-xs font-medium flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                <Key className="h-3 w-3 text-teal-600 dark:text-teal-400" />
              </div>
              Chave PIX
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="pix-key"
              placeholder={currentConfig.placeholder}
              value={pixKey}
              onChange={(e) => setPixKey(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
              className="h-9 rounded-lg bg-muted/50 border-0 text-sm focus-visible:ring-2 focus-visible:ring-emerald-500 transition-all font-mono"
            />
          </div>

          {/* Mensagem opcional */}
          <div className="space-y-1.5">
            <Label htmlFor="pix-message" className="text-xs font-medium flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-lime-100 dark:bg-lime-900/30 flex items-center justify-center">
                <Send className="h-3 w-3 text-lime-600 dark:text-lime-400" />
              </div>
              Mensagem
              <span className="text-xs text-muted-foreground font-normal">(opcional)</span>
            </Label>
            <Textarea
              id="pix-message"
              placeholder="Ex: Pagamento referente à..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={sending}
              rows={2}
              className="min-h-[60px] rounded-lg bg-muted/50 border-0 text-sm focus-visible:ring-2 focus-visible:ring-emerald-500 transition-all resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="gap-2 sm:gap-2 pt-1">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={sending}
            className="rounded-lg h-9 text-sm"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSend}
            disabled={!isValid || sending}
            className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-md shadow-emerald-500/20 rounded-lg h-9 px-4 text-sm transition-all duration-300"
          >
            {sending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <QrCode className="h-3.5 w-3.5 mr-1.5" />
                Enviar PIX
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
